import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Button,
  Stack,
  Chip,
  CircularProgress,
} from '@mui/material'
import { useCurrency } from '../../context/CurrencyContext'
import { fetchRooms, type Room } from '../../services/roomService'

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
}

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
    },
  },
}

const FeaturedRooms = () => {
  const navigate = useNavigate()
  const { convertPrice } = useCurrency()
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  })
  const [rooms, setRooms] = useState<Room[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const loadRooms = async () => {
      try {
        const roomsData = await fetchRooms()
        // Get 3 random available rooms
        const availableRooms = roomsData.filter(room => room.status === 'available')
        const randomRooms = availableRooms
          .sort(() => Math.random() - 0.5)
          .slice(0, 3)
        setRooms(randomRooms)
      } catch (error) {
        console.error('Error loading rooms:', error)
      } finally {
        setLoading(false)
      }
    }
    loadRooms()
  }, [])

  const handleViewDetails = (room: Room) => {
    navigate(`/rooms/${room.id}`)
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40vh' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (rooms.length === 0) {
    return null
  }

  return (
    <Box
      component={motion.div}
      ref={ref}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      variants={staggerContainer}
      sx={{
        pt: { xs: 4, md: 6 },
        pb: { xs: 4, md: 6 },
        bgcolor: 'background.default',
      }}
    >
      <Container maxWidth="xl">
        <Typography
          variant="h2"
          align="center"
          gutterBottom
          sx={{
            mb: { xs: 4, md: 5 },
            fontFamily: '"Playfair Display", serif',
            fontSize: { xs: '2rem', md: '2.75rem' },
          }}
        >
          Featured Rooms
        </Typography>
        <Grid container spacing={4}>
          {rooms.map((room) => (
            <Grid
              item
              key={room.id}
              xs={12}
              md={4}
              component={motion.div}
              variants={fadeInUp}
            >
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 2,
                  overflow: 'hidden',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                  },
                  position: 'relative',
                }}
              >
                <CardMedia
                  component="img"
                  height="240"
                  image={room.images[0]}
                  alt={room.name}
                  sx={{
                    objectFit: 'cover',
                  }}
                />
                <CardContent sx={{ flexGrow: 1, p: 3 }}>
                  <Typography
                    variant="h5"
                    gutterBottom
                    sx={{
                      fontFamily: '"Playfair Display", serif',
                      fontWeight: 600,
                      color: 'primary.main',
                    }}
                  >
                    {room.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mb: 2,
                      minHeight: 48,
                    }}
                  >
                    {room.type}
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                      mb: 2,
                      flexWrap: 'wrap',
                      '& > *': { mb: 1 },
                    }}
                  >
                    {room.amenities.slice(0, 3).map((amenity) => (
                      <Chip
                        key={amenity}
                        label={amenity}
                        size="small"
                        variant="outlined"
                        sx={{
                          borderRadius: 1,
                          borderColor: 'rgba(0,0,0,0.1)',
                        }}
                      />
                    ))}
                  </Stack>
                  <Box sx={{ mt: 'auto' }}>
                    <Typography
                      variant="h6"
                      color="primary"
                      sx={{
                        fontWeight: 700,
                        mb: 2,
                      }}
                    >
                      {convertPrice(room.basePrice)}
                      <Typography
                        component="span"
                        variant="body2"
                        sx={{
                          color: 'text.secondary',
                          ml: 1,
                        }}
                      >
                        / night
                      </Typography>
                    </Typography>
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={() => handleViewDetails(room)}
                      sx={{
                        borderRadius: 1.5,
                        py: 1,
                        textTransform: 'none',
                      }}
                    >
                      View Details
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  )
}

export default FeaturedRooms 